/**
 * LEDOverridesPanel - Configure per-key LED overrides
 * Spec §5.12: LED overrides per key
 */

import { useState, useEffect } from 'react';
import { useConfigStore } from '@/store/configStore';
import type { LEDState } from '@/types/config';

function ColorPicker({
  label,
  color,
  onChange,
}: {
  label: string;
  color: [number, number, number];
  onChange: (color: [number, number, number]) => void;
}) {
  // Convert RGB array to hex for color input
  const toHex = (rgb: [number, number, number]) =>
    '#' + rgb.map((c) => c.toString(16).padStart(2, '0')).join('');

  // Convert hex to RGB array
  const fromHex = (hex: string): [number, number, number] => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1] ?? '0', 16), parseInt(result[2] ?? '0', 16), parseInt(result[3] ?? '0', 16)]
      : [0, 0, 0];
  };

  return (
    <div style={{ marginBottom: '8px' }}>
      <label style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}>{label}:</label>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          type="color"
          value={toHex(color)}
          onChange={(e) => onChange(fromHex(e.target.value))}
          style={{ width: '40px', height: '30px', border: 'none', cursor: 'pointer' }}
        />
        <span style={{ fontSize: '12px', color: '#6c757d' }}>
          RGB({color[0]}, {color[1]}, {color[2]})
        </span>
      </div>
    </div>
  );
}

function BrightnessSlider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div style={{ marginBottom: '8px' }}>
      <label style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}>
        {label}: {Math.round(value * 100)}%
      </label>
      <input
        type="range"
        min={0}
        max={100}
        value={Math.round(value * 100)}
        onChange={(e) => onChange(parseInt(e.target.value, 10) / 100)}
        style={{ width: '100%' }}
      />
    </div>
  );
}

export function LEDOverridesPanel() {
  const selectedKey = useConfigStore((state) => state.selectedKey);
  const activeLayer = useConfigStore((state) => state.activeLayer);
  const ledOverrides = useConfigStore((state) => state.ledOverrides);
  const setSelectedKey = useConfigStore((state) => state.setSelectedKey);
  const addLEDOverride = useConfigStore((state) => state.addLEDOverride);
  const removeLEDOverride = useConfigStore((state) => state.removeLEDOverride);

  // Find existing LED override for selected key
  const existingOverride = selectedKey
    ? ledOverrides.find(
        (o) => o.pos.q === selectedKey.q && o.pos.r === selectedKey.r && o.layer === activeLayer
      )
    : undefined;

  // Local state for editing
  const [idleState, setIdleState] = useState<LEDState>({
    color: [255, 0, 0],
    brightness: 0.8,
  });
  const [pressedState, setPressedState] = useState<LEDState>({
    color: [255, 100, 100],
    brightness: 1.0,
  });
  const [usePressedState, setUsePressedState] = useState(true);

  // Sync local state with existing override
  useEffect(() => {
    if (existingOverride) {
      setIdleState(existingOverride.idle);
      if (existingOverride.pressed) {
        setPressedState(existingOverride.pressed);
        setUsePressedState(true);
      } else {
        setUsePressedState(false);
      }
    } else {
      setIdleState({ color: [255, 0, 0], brightness: 0.8 });
      setPressedState({ color: [255, 100, 100], brightness: 1.0 });
      setUsePressedState(true);
    }
  }, [existingOverride, selectedKey]);

  const handleSave = () => {
    if (!selectedKey) return;
    addLEDOverride({
      pos: selectedKey,
      layer: activeLayer,
      idle: idleState,
      ...(usePressedState && { pressed: pressedState }),
    });
  };

  const handleRemove = () => {
    if (!selectedKey) return;
    removeLEDOverride(selectedKey, activeLayer);
  };

  const handleClearSelection = () => {
    setSelectedKey(null);
  };

  return (
    <>
      {/* Selected key info */}
      {selectedKey ? (
        <>
          <div
            style={{
              padding: '8px',
              backgroundColor: '#e8f5e9',
              borderRadius: '4px',
              marginBottom: '12px',
            }}
          >
            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
              Selected Key: ({selectedKey.q}, {selectedKey.r})
            </div>
            <div style={{ fontSize: '12px', color: '#6c757d' }}>Layer: {activeLayer}</div>
            {existingOverride && (
              <div style={{ fontSize: '12px', color: '#2e7d32', marginTop: '4px' }}>
                Has LED override
              </div>
            )}
          </div>

          {/* Idle state editor */}
          <div
            style={{
              marginBottom: '16px',
              padding: '8px',
              border: '1px solid #e9ecef',
              borderRadius: '4px',
            }}
          >
            <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
              Idle State (key not pressed)
            </div>
            <ColorPicker
              label="Color"
              color={idleState.color}
              onChange={(color) => setIdleState({ ...idleState, color })}
            />
            <BrightnessSlider
              label="Brightness"
              value={idleState.brightness}
              onChange={(brightness) => setIdleState({ ...idleState, brightness })}
            />
          </div>

          {/* Pressed state toggle */}
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={usePressedState}
                onChange={(e) => setUsePressedState(e.target.checked)}
              />
              <span style={{ fontSize: '14px' }}>Define separate pressed state</span>
            </label>
          </div>

          {/* Pressed state editor */}
          {usePressedState && (
            <div
              style={{
                marginBottom: '16px',
                padding: '8px',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
              }}
            >
              <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                Pressed State (key pressed)
              </div>
              <ColorPicker
                label="Color"
                color={pressedState.color}
                onChange={(color) => setPressedState({ ...pressedState, color })}
              />
              <BrightnessSlider
                label="Brightness"
                value={pressedState.brightness}
                onChange={(brightness) => setPressedState({ ...pressedState, brightness })}
              />
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button onClick={handleSave} style={buttonStyle}>
              {existingOverride ? 'Update' : 'Add'} LED Override
            </button>
            {existingOverride && (
              <button onClick={handleRemove} style={{ ...buttonStyle, backgroundColor: '#dc3545' }}>
                Remove
              </button>
            )}
            <button onClick={handleClearSelection} style={{ ...buttonStyle, backgroundColor: '#6c757d' }}>
              Clear Selection
            </button>
          </div>
        </>
      ) : (
        <div style={{ color: '#6c757d', fontStyle: 'italic' }}>
          Click a key on the keyboard to select it for LED override configuration.
        </div>
      )}

      {/* LED override count */}
      <div style={{ marginTop: '16px', fontSize: '12px', color: '#6c757d' }}>
        Total LED overrides: {ledOverrides.length}
      </div>
    </>
  );
}

const buttonStyle: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: '14px',
  color: '#fff',
  backgroundColor: '#28a745',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};
